/** Browser side of the account/songs API. Same origin, session cookie. */
import { useEffect, useState } from 'react';
import type { Me, ServerInfo, SongMeta } from '../shared/account.ts';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError((data as { error?: string }).error ?? `Error ${res.status}`, res.status);
  return data as T;
}

export const api = {
  info: () => call<ServerInfo>('GET', '/api/info'),
  me: () => call<Me>('GET', '/api/me').catch((e) => (e instanceof ApiError && e.status === 401 ? null : Promise.reject(e))),
  sendCode: (email: string) => call<{ ok: true }>('POST', '/api/auth/code', { email }),
  verify: (email: string, code: string, marketing: boolean) => call<Me>('POST', '/api/auth/verify', { email, code, marketing }),
  logout: () => call<{ ok: true }>('POST', '/api/auth/logout', {}),
  setMarketing: (on: boolean) => call<Me>('POST', '/api/me/marketing', { on }),
  deleteAccount: () => call<{ ok: true }>('DELETE', '/api/me', { confirm: true }),
  songs: () => call<SongMeta[]>('GET', '/api/songs'),
  song: (id: string) => call<{ name: string; updatedAt: number; state: unknown }>('GET', `/api/songs/${encodeURIComponent(id)}`),
  createSong: (name: string, state: unknown) => call<SongMeta>('POST', '/api/songs', { name, state }),
  saveSong: (id: string, name: string, state: unknown) => call<SongMeta>('PUT', `/api/songs/${encodeURIComponent(id)}`, { name, state }),
  renameSong: (id: string, name: string) => call<{ ok: true }>('PATCH', `/api/songs/${encodeURIComponent(id)}`, { name }),
  deleteSong: (id: string) => call<{ ok: true }>('DELETE', `/api/songs/${encodeURIComponent(id)}`),
  credits: () =>
    call<{ name: string; author: string; source: string; license: string; attribution: string | null; files: number }[]>('GET', '/api/credits'),
};

let infoCache: Promise<ServerInfo> | null = null;
/** Server mode and links, fetched once per page. */
export function serverInfo(): Promise<ServerInfo> {
  infoCache ??= api.info().catch(() => ({
    lan: [], https: location.protocol === 'https:', platform: '', port: 0, public: false, authRequired: false, publicUrl: '', donateUrl: '', contactEmail: '', operator: '', hosting: '',
  }));
  return infoCache;
}

export function useServerInfo(): ServerInfo | null {
  const [info, setInfo] = useState<ServerInfo | null>(null);
  useEffect(() => {
    void serverInfo().then(setInfo);
  }, []);
  return info;
}

/** Signed-in account: undefined while checking, null when signed out. */
export function useMe(): [Me | null | undefined, (m: Me | null) => void] {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  useEffect(() => {
    api.me().then(setMe, () => setMe(null));
  }, []);
  return [me, setMe];
}
